import { Outlet, createFileRoute } from "@tanstack/react-router"
import {
  Group,
  Burger,
  Divider,
  Center,
  Loader,
  ActionIcon,
} from "@mantine/core"
import { useDisclosure, useHover, useTimeout } from "@mantine/hooks"
import { InstanceLogoLink } from "./-components/instance-logo"
import { NavigationTree } from "./-components/navigation-tree"
import { UserSectionContent } from "./-components/user-section-content"
import { useBreakpoints } from "../utils/use-breakpoints"
import { AttachmentUploadContextProvider } from "../attachment-api/components/attachment-upload-context-provider"
import { ScrollContainer } from "../-components/scroll-container"
import "./index.css"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { ensureClientPushSubscription } from "@/utils/subscriptions"
import {
  chatHubConnection,
  initializeSignalR,
} from "./-components/chat/chatHub"
import {
  SuspenseWithErrorBoundary,
  SuspenseWithFallback,
} from "@components/suspense"
import { useUserLocalStorage } from "./app/-components/use-user-local-storage"
import { IconPin, IconPinFilled } from "@tabler/icons-react"

export const Route = createFileRoute("/app")({
  component: () => (
    <SuspenseWithFallback>
      <Shell />
    </SuspenseWithFallback>
  ),
})

export function Shell() {
  const ensurePush = React.useRef<boolean>(true)
  const navigate = Route.useNavigate()
  const { xs } = useBreakpoints({
    xs: { defaultValue: true, options: { getInitialValueInEffect: false } },
  })
  const { data: authStatus } = useSuspenseQueryGet(
    "/api/Authentication/GetAuthStatus",
    null
  )
  const { data: navigationInfo } = useQueryGet(
    "/api/NavigationTree/GetNavigationInfo",
    null,
    {
      enabled: authStatus.loggedIn || authStatus.publicModeEnabled,
    }
  )
  const { data: features } = useQueryGet(
    "/api/Settings/GetFeatureSettings",
    null,
    { enabled: authStatus.loggedIn }
  )
  const [pinned, setPinned] = useUserLocalStorage<boolean>({
    key: "sidebar-pinned",
    defaultValue: true,
  })
  const togglePinned = () => setPinned((prev) => !prev)
  const { hovered, ref } = useHover()

  const [menuOpened, { open: openMenu, close: closeMenu }] =
    useDisclosure(false)

  const [burgerMenuOpened, { toggle: toggleBurgerMenu, close }] =
    useDisclosure(false)

  const menuTimeout = useTimeout(openMenu, 500)

  const asideOpened = xs ? burgerMenuOpened : menuOpened || pinned || hovered

  React.useEffect(() => {
    if (authStatus.loggedIn) {
      if (
        (features?.chatEnabled || features?.feedEnabled) &&
        !chatHubConnection.connectionId
      ) {
        initializeSignalR()
      }
      if (
        chatHubConnection.connectionId &&
        !(features?.feedEnabled || features?.chatEnabled)
      ) {
        chatHubConnection.stop()
      }
      try {
        if (ensurePush.current && Notification.permission !== "denied") {
          if (Notification.permission === "default") {
            Notification.requestPermission()
          }
          ensureClientPushSubscription()
          ensurePush.current = false
        }
      } catch (err) {
        console.error(err)
      }
    }
  }, [authStatus.loggedIn, features])

  React.useEffect(() => {
    if (location.pathname === "/app" || location.pathname === "/app/") {
      const defaultRoute = navigationInfo?.permittedRoutes[0]?.route
      if (authStatus.loggedIn) {
        if (authStatus.requireMfaValidation) {
          navigate({
            to: "/authenticate/mfa/select-authentication-factor",
            search: { returnUrl: undefined },
          })
        } else {
          navigate({
            to: defaultRoute ?? "/app/dashboard",
            replace: true,
          })
        }
      } else if (authStatus.publicModeEnabled) {
        if (defaultRoute) {
          navigate({
            to: defaultRoute,
            replace: true,
          })
        }
      } else {
        navigate({
          to: "/authenticate/login",
          replace: true,
        })
      }
    }
  }, [
    authStatus.requireMfaValidation,
    authStatus.publicModeEnabled,
    authStatus.loggedIn,
    navigationInfo?.permittedRoutes,
  ])

  // avoid rendering any app internal components before redirect to mfa validation
  if (authStatus.requireMfaValidation) {
    return null
  }

  return (
    <AttachmentUploadContextProvider>
      <div className="shell-root">
        {xs && (
          <>
            <div className="shell-header">
              <Group
                h="100%"
                px="xs"
                justify="space-between"
                style={{ flexDirection: "row-reverse" }}
              >
                <Burger opened={burgerMenuOpened} onClick={toggleBurgerMenu} />
                <InstanceLogoLink />
              </Group>
            </div>
            <Divider />
          </>
        )}

        <div className="shell-body">
          <aside
            className="shell-sidebar"
            data-opened={asideOpened}
            data-mobile={xs}
            style={{
              width: asideOpened ? (xs ? "100%" : 300) : xs ? 0 : 60,
              zIndex: asideOpened && xs ? 190 : undefined,
            }}
            ref={ref}
          >
            {!xs && (
              <>
                {asideOpened ? (
                  <Group
                    h="60px"
                    px="sm"
                    justify="space-between"
                    style={{ flexDirection: "row-reverse" }}
                    wrap="nowrap"
                  >
                    <ActionIcon variant="transparent" onClick={togglePinned}>
                      {pinned ? <IconPinFilled /> : <IconPin />}
                    </ActionIcon>
                    <InstanceLogoLink />
                  </Group>
                ) : (
                  <Center h={60}>
                    <Burger></Burger>
                  </Center>
                )}
                <Divider />
              </>
            )}

            <div className="shell-sidebar-content">
              <ScrollContainer
                flex={1}
                direction="column"
                style={{
                  overflowX: "hidden",
                  overflowY: asideOpened ? "auto" : "hidden",
                }}
              >
                <SuspenseWithErrorBoundary>
                  <NavigationTree
                    navigationTree={navigationInfo?.navTree ?? []}
                    iconsOnly={!asideOpened && !xs}
                    onNodeClick={xs ? close : undefined}
                  />
                </SuspenseWithErrorBoundary>
              </ScrollContainer>
              <Divider />
              <UserSectionContent
                onNavigate={() => {
                  if (xs && asideOpened) {
                    close()
                  }
                }}
                iconOnly={!asideOpened}
                onClose={closeMenu}
                opened={menuOpened}
                onOpen={() => {
                  if (!asideOpened) {
                    menuTimeout.start()
                  } else {
                    openMenu()
                  }
                }}
              />
            </div>
          </aside>
          <Divider orientation="vertical" />

          <main className="shell-main">
            <SuspenseWithErrorBoundary
              fallback={
                <Center style={{ height: "100%" }}>
                  <Loader size={30} />
                </Center>
              }
            >
              <Outlet />
            </SuspenseWithErrorBoundary>
          </main>
        </div>
      </div>
    </AttachmentUploadContextProvider>
  )
}
